import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import useHashParam from 'use-hash-param';

import { Config } from '../services/Config';
import { isSupportedStripeRegion } from '../services/Stripe';
import { useAuth } from '../services/Auth';
import { useRouter, useSiteRoutes } from '../services/Navigation';
import { DevCheckoutLaunchDrawer } from '../components/app/DevCheckoutLaunchDrawer';
import { AppLayoutContainer } from '../components/app/AppLayout';
import {
  CheckoutPageContainer,
  useCheckoutParams,
} from '../components/checkout/CheckoutPage';
import { PaymentProviderUnconfiguredErrorScreen } from '../components/checkout/PaymentProviderUnconfiguredErrorScreen';
import { PaymentRegionUnsupportedErrorScreen } from '../components/checkout/PaymentRegionUnsupportedErrorScreen';

export const Route = createFileRoute('/checkout')({
  component: CheckoutRoute,
});

function CheckoutRoute() {
  const router = useRouter();
  const { routes } = useSiteRoutes();
  const [accessToken, setAccessToken] = useHashParam('accessToken', '');
  const { setCredentials, logout } = useAuth();
  const {
    companyId,
    groupId,
    productFamily,
    apiKey,
    region,
    isRegionSupported,
    subscriptionId,
    setCompanyId,
    setGroupId,
    setProductFamily,
    setRegion,
    asQueryString,
  } = useCheckoutParams();

  const shouldCaptureParamsFromDevUser =
    !companyId || !groupId || !productFamily || !region || !apiKey;

  return (
    <AppLayoutContainer
      helpAndSupportUrl={Config.APPLICATION_GENERAL_HELPARTICLE_URL}
      siteName={Config.APPLICATION_SHORT_DISPLAY_NAME}
    >
      {!!Config.APPLICATION_FEATUREFLAG_FAKECHECKOUTLAUNCHER && (
        <DevCheckoutLaunchDrawer
          initialData={{
            accessToken,
            companyId,
            groupId,
            productFamily,
            region: (isSupportedStripeRegion(region) && region) || null,
          }}
          isOpen={shouldCaptureParamsFromDevUser}
          onLaunchClick={({
            accessToken,
            companyId,
            groupId,
            productFamily,
            region,
          }) => {
            setAccessToken(accessToken);
            setCredentials({ accessToken });
            setRegion(region || 'None');
            setCompanyId(companyId);
            setProductFamily(productFamily);
            setGroupId(groupId);
          }}
        />
      )}

      <CheckoutPageContainer
        apiKey={apiKey}
        companyId={companyId}
        groupId={groupId}
        insightsInformationUrl={
          Config.APPLICATION_PRODUCTINFORMATION_INSIGHTS_URL
        }
        productFamily={productFamily}
        region={region}
        subscriptionId={subscriptionId}
        subscriptionLegalInfoUrl={
          Config.APPLICATION_PRODUCTINFORMATION_INSIGHTS_SUBSCRIPTIONCONDITIONS_URL
        }
        termsOfUseInfoUrl={
          Config.APPLICATION_PRODUCTINFORMATION_INSIGHTS_TERMSANDCONDITIONS_URL
        }
        onOrderSuccess={(subscriptionId: string) => {
          router.push({
            pathname: routes.checkoutConfirmation.path,
            hash: `?${asQueryString({ subscriptionId, companyId })}`,
          });
        }}
      />

      {/* If we support the region but there's a missing ENV_VAR for the apikey */}
      {!apiKey && !!isRegionSupported && (
        <PaymentProviderUnconfiguredErrorScreen
          fullscreen={!Config.APPLICATION_FEATUREFLAG_FAKECHECKOUTLAUNCHER}
          onResolveErrorClick={() => {
            logout({ reasonCode: 'USER_INITIATED' });
          }}
        />
      )}

      {/* If we don't support the region */}
      {!isRegionSupported && (
        <PaymentRegionUnsupportedErrorScreen
          fullscreen={!Config.APPLICATION_FEATUREFLAG_FAKECHECKOUTLAUNCHER}
          onResolveErrorClick={() => {
            logout({ reasonCode: 'USER_INITIATED' });
          }}
        />
      )}
    </AppLayoutContainer>
  );
}
